import { useEffect, useRef, useState } from "react";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { loadGoogleMaps } from "../lib/maps";
import { useIsMobile } from "../lib/useIsMobile";

export function AllRoutesMap({ routes, loading, matchedClientIds = [] }) {
  const isMobile = useIsMobile();
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const openIw = useRef(null);      // the one open trip InfoWindow
  const selLine = useRef(null);     // the tapped (highlighted) polyline
  const [clients, setClients] = useState([]);

  useEffect(()=>{
    if (!matchedClientIds.length) { setClients([]); return; }
    // Only fetch clients that are part of matched trips in this date range
    const ids = matchedClientIds.join(",");
    fetch(`${SUPABASE_URL}/rest/v1/clients?id=in.(${ids})&lat=not.is.null&lng=not.is.null&select=id,name,lat,lng`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
    }).then(r=>r.json()).then(rows=>{ if(Array.isArray(rows)) setClients(rows); });
  },[matchedClientIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    if (!mapRef.current || (!routes.length && !loading)) return;

    loadGoogleMaps().then(()=>{
      if (!mapRef.current) return;

      if (!mapObj.current) {
        mapObj.current = new window.google.maps.Map(mapRef.current, {
          center: { lat:39.95, lng:-75.16 }, zoom:11, mapTypeId:"roadmap",
          gestureHandling: isMobile ? "greedy" : "auto",   // one-finger pan on phones; desktop unchanged
          styles:[{featureType:"poi",elementType:"labels",stylers:[{visibility:"off"}]}],
        });
      }

      // Clear overlays
      if (mapObj.current._overlays) mapObj.current._overlays.forEach((o:any)=>o.setMap(null));
      mapObj.current._overlays = [];
      try { openIw.current?.close(); } catch (e) {}
      openIw.current = null; selLine.current = null;

      const unhighlight = (l) => { if (l) l.setOptions({strokeWeight:3,strokeOpacity:0.7}); };

      const bounds = new window.google.maps.LatLngBounds();

      // Draw all routes
      routes.forEach(({trip, route}:any)=>{
        if (!route || route.length < 2) return;
        const path = route.map((c)=>({ lat:Number(c[1]), lng:Number(c[0]) }));
        const color = trip.trip_type==="business" ? "#22C55E" : "#3B82F6";
        const line = new window.google.maps.Polyline({
          path, geodesic:true, strokeColor:color,
          strokeOpacity:0.7, strokeWeight:3, map:mapObj.current,
        });
        const iw = new window.google.maps.InfoWindow({
          content:`<div style="font-size:12px;padding:2px">
            <strong>${new Date(trip.start_time).toLocaleDateString()} ${new Date(trip.start_time).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</strong><br>
            ${Number(trip.distance_miles).toFixed(1)} mi · ${trip.trip_type==="business"?"🏢 Business":"🏠 Personal"}<br>
            ${trip.matched_client_name?`Client: ${trip.matched_client_name}`:""}
          </div>`
        });
        // An InfoWindow with neither anchor nor position is never displayed — the old
        // `iw.open(map)` silently showed nothing on every device. Anchor it at the tap
        // point, keep one open at a time, and highlight the tapped line (touch has no hover).
        line.addListener("click",(e)=>{
          try { openIw.current?.close(); } catch (err) {}
          if (selLine.current && selLine.current!==line) unhighlight(selLine.current);
          selLine.current = line;
          line.setOptions({strokeWeight:5,strokeOpacity:1});
          if (e?.latLng) iw.setPosition(e.latLng);
          iw.open(mapObj.current);
          openIw.current = iw;
        });
        iw.addListener("closeclick",()=>{ if (selLine.current===line) { unhighlight(line); selLine.current=null; } openIw.current=null; });
        line.addListener("mouseover",()=>{ line.setOptions({strokeWeight:5,strokeOpacity:1}); });
        line.addListener("mouseout",()=>{ if (selLine.current!==line) unhighlight(line); });
        mapObj.current._overlays.push(line);
        path.forEach((p:any)=>bounds.extend(p));
      });

      // Client markers
      clients.forEach((c)=>{
        const pos = { lat:Number(c.lat), lng:Number(c.lng) };
        // All clients shown are visited (filtered above)
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="11" fill="#F59E0B" stroke="white" stroke-width="1.5"/>
          <text x="12" y="16" text-anchor="middle" font-size="10">🐕</text>
        </svg>`;
        const marker = new window.google.maps.Marker({
          position:pos, map:mapObj.current, title:c.name, zIndex:10,
          icon:{ url:`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize:new window.google.maps.Size(24,24), anchor:new window.google.maps.Point(12,12) },
        });
        const iw = new window.google.maps.InfoWindow({
          content:`<div style="font-size:12px"><strong>${c.name}</strong><br><span style='color:#B45309'>✓ Visited this period</span></div>`
        });
        marker.addListener("click",()=>{ try { openIw.current?.close(); } catch (err) {} iw.open(mapObj.current,marker); openIw.current=iw; });
        bounds.extend(pos);
        mapObj.current._overlays.push(marker);
      });

      if (!bounds.isEmpty()) mapObj.current.fitBounds(bounds, 40);
    });
  },[routes, clients]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{display:"flex",gap:"4px 16px",flexWrap:"wrap",fontSize:11,color:"#8993a4",marginBottom:8}}>
        <span style={{whiteSpace:"nowrap"}}><span style={{color:"#22C55E",fontWeight:700}}>━━</span> Business</span>
        <span style={{whiteSpace:"nowrap"}}><span style={{color:"#3B82F6",fontWeight:700}}>━━</span> Personal</span>
        <span style={{whiteSpace:"nowrap"}}><span style={{color:"#F59E0B"}}>🐕</span> Visited client</span>
        <span style={{color:"#42526e",whiteSpace:"nowrap"}}>{isMobile?"Tap a line for trip details":"Click any line for trip details"}</span>
      </div>
      {loading&&!routes.length&&<div style={{padding:20,textAlign:"center",color:"#8993a4",fontSize:12}}>Loading routes...</div>}
      {/* Phone: ~55vh so the page around the map stays reachable (greedy gestures). */}
      <div ref={mapRef} style={{width:"100%",height:isMobile?"55vh":500,borderRadius:8,background:"#e8eaed",display:routes.length?"block":"none"}}/>
    </div>
  );
}
